import { animated, useTransition } from "@react-spring/web";
import React, {
	forwardRef,
	memo,
	PropsWithChildren,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react";

import "./drag-image.css";

export interface IDragImageRef {
	el: HTMLDivElement | null;

	start(url: string, x: number, y: number, dx?: number, dy?: number, urlRight?: string): void;

	moveTo(x: number, y: number): void;

	end(dx?: number, dy?: number): void;

	isDragging(): boolean;
}

/**
 * Component that can be used for dragging images
 * Use together with useDragImage
 *  Later:
 * - we can use this by multiple image draggers
 * - we can expose logic to help animating
 */
export const DragImage = memo(
	forwardRef<IDragImageRef, PropsWithChildren>((_props, parentRef) => {
		const ref = useRef<HTMLDivElement>(null);
		const [dragImageUrl, setDragImageUrl] = useState<string | undefined>(undefined);
		const [dragImageUrlRight, setDragImageUrlRight] = useState<string | undefined>(undefined);
		const isDraggingRef = useRef(false); // Store in a ref so the returned imperativeHandle stays stable

		/** An object with information used at useTransition functions */
		const mutable = useMemo<{
			/** Offset from center (for animation) */
			dx: number | undefined;
			dy: number | undefined;
		}>(
			() => ({
				dx: 0,
				dy: 0,
			}),
			[],
		);

		useImperativeHandle(
			parentRef,
			() => ({
				get el() {
					return ref.current;
				},

				start(url, x, y, dx = 0, dy = 0, urlRight = "") {
					// Set information for enter animation
					mutable.dx = dx;
					mutable.dy = dy;

					isDraggingRef.current = !!url;
					setDragImageUrl(url);
					if (urlRight) setDragImageUrlRight(urlRight);

					const dragImageEl = ref.current;
					if (dragImageEl) {
						// Don't use animation for following finger
						dragImageEl.style.transform = `translate(${x}px, ${y}px)`;
					}
				},

				/** Update position */
				moveTo(x, y) {
					const dragImageEl = ref.current;
					if (dragImageEl) {
						// Don't use animation for following finger
						dragImageEl.style.transform = `translate(${x}px, ${y}px)`;
					}
				},

				end(dx, dy) {
					mutable.dx = dx;
					mutable.dy = dy;
					isDraggingRef.current = false;
					setDragImageUrl(undefined);
					setDragImageUrlRight(undefined);
				},

				isDragging() {
					return isDraggingRef.current;
				},
			}),
			[mutable],
		);

		const transitions = useTransition(dragImageUrl, {
			from: () => {
				const { dx, dy } = mutable;
				return {
					opacity: 0,
					x: dx,
					y: dy,
				};
			},
			enter: {
				opacity: 1,
				x: 0,
				y: 0,
			},
			leave: () => {
				const { dx, dy } = mutable;
				if (dx === undefined) return undefined; // only animate if dx is passed
				return {
					opacity: 0,
					x: dx ?? 0,
					y: dy ?? 0,
				};
			},
			config: {
				duration: 200,
			},
		});

		return (
			<div className="drag-image" ref={ref}>
				{
					// Enter and leave animation
					transitions((props, url) =>
						url ? (
							<animated.div className="drag-image__anim" style={props}>
								{/* eslint-disable-next-line jsx-a11y/alt-text */}
								<img src={url} className="drag-image__img" />
								{dragImageUrlRight ? (
									// eslint-disable-next-line jsx-a11y/alt-text
									<img src={dragImageUrlRight} className="drag-image__img" />
								) : null}
							</animated.div>
						) : null,
					)
				}
			</div>
		);
	}),
);
DragImage.displayName = "DragImage";
